import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT } from '../utils/environment'
import {
  DashboardSearchCriteria,
  EmbeddedDashboardPayload,
  GetEntityDataByDashboardFilter,
  GetInteractiveDashboardDataPayload,
  TableFilterApiBody,
} from '../types'
import { validateApiParams } from '../utils/functions'
import { resolveEntityName } from '../utils/entities.utils'

const LAYOUT_ENDPOINT = `${API_END_POINT.V3_ENTITY_MANAGER}layout`

export const getAllClientProgramsCorrespondingToUser = async (
  user_id?: string
) => {
  return await axiosInstance.get(
    `${API_END_POINT.CORE}v1/dashboard/client_program_data`,
    {
      params: {
        userId: user_id,
      },
    }
  )
}

export const getLayoutDashboardAPI = async (
  user_id: string,
  program_id: string,
  isSupervisor: boolean
) => {
  return axiosInstance.get(`${API_END_POINT.CORE}v1/dashboard/all`, {
    params: {
      user_id,
      program_id,
      isSupervisor,
    },
  })
}

export const getDashboardLayoutByIdApi = async <
  T extends { entityId: string; layoutId: string }
>(
  payload: T
) => {
  const { entityId, layoutId } = payload

  const url = `${LAYOUT_ENDPOINT}/${layoutId}?DashboardLayoutConfig`
  const params = { entityId }
  validateApiParams(
    { ...params, layoutId: layoutId },
    {
      endpoint: url,
      method: 'GET',
    }
  )

  return await axiosInstance.get(url, { params: params })
}

export const getEntityDataDashboardAPI = async (
  action: GetEntityDataByDashboardFilter
) => {
  const { filter, page, size, ...restPayload } = action.payload

  const { userId, ...restParams } = restPayload
  const params = action.is_agent ? { ...restParams, userId } : restParams

  const requestBody = {
    page: page,
    size: size,
    filters: filter ?? [],
  }

  return await axiosInstance.post(
    `${API_END_POINT.CORE}v2/dashboard/layout-data`,
    requestBody,
    { params }
  )
}

export const dashboardPageableQueuesApi = async (payload: {
  page?: number
  size?: number
  sort?: string
  filter?: TableFilterApiBody
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/queues/advance?DashboardLayoutConfig`,
    payload?.filter,
    {
      params: {
        page: payload?.page,
        size: payload?.size,
        sort: payload?.sort,
      },
    }
  )
}

export const getCaseSummaryTileAPI = async (action: {
  program_id: string
  user_id?: string
}) => {
  const params = new URLSearchParams()

  params.append('programId', action.program_id)
  if (action.user_id) {
    params.append('userId', action.user_id)
  }

  return await axiosInstance.get(
    `${API_END_POINT.CORE}v1/dashboard/tiles-data?${params.toString()}`
  )
}

export const getClickableConfigQueueAPI = async (
  queueId: number,
  slaFilter: string,
  page: number,
  size: number,
  payload: { filter_criteria: DashboardSearchCriteria[] }
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/queues/dashboard-record/${queueId}`,
    payload,
    {
      params: {
        slaFilterType: slaFilter,
        page: page,
        size: size,
      },
    }
  )
}

export const getInteractiveDashboardData = async (
  payload: GetInteractiveDashboardDataPayload
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/embedded/dashboard`,
    payload
  )
}

export const getEmbeddedDashboardData = async (
  payload: EmbeddedDashboardPayload,
  entityName: string
) => {
  const resolvedEntityName = resolveEntityName(entityName, payload.entity_id)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${resolvedEntityName}`
  validateApiParams(
    { entity_name: resolvedEntityName },
    { endpoint, method: 'POST' }
  )
  return await axiosInstance.post(endpoint, payload)
}

export const getUrgencyIndexFlags = async (programId: number) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/embedded/dashboard/urgency-index/entity-flags/${programId}`
  )
}
